import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Chip, IconButton, Button, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
    ArrowBack as BackIcon, EmojiEvents as TrophyIcon, Delete as DeleteIcon,
    HowToVote as NominateIcon
} from '@mui/icons-material';
import { useAuth } from '../Auth';
import { useSnackbar } from '../context/SnackbarContext';
import { formatDate } from '../utils/dateUtils';
import api from '../api/axiosInstance';

const STATUS_COLOURS = {
    pending: 'default',
    shortlisted: 'info',
    winner: 'success',
    declined: 'error'
};

// Students are stored as JSONB payloads keyed by the import schema ids
const studentName = (payload) => {
    if (!payload) return 'Unknown student';
    const first = payload.id_3 || payload.id_4 || '';
    const last = payload.id_5 || '';
    return `${first} ${last}`.trim() || 'Unnamed student';
};

export default function CeremonyDetail() {
    const { ceremonyId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const showSnackbar = useSnackbar();
    const canManage = user?.role === 'admin' || user?.role === 'superuser';

    const [loading, setLoading] = useState(true);
    const [ceremony, setCeremony] = useState(null);
    const [awards, setAwards] = useState([]);
    const [nominations, setNominations] = useState([]);
    const [filter, setFilter] = useState('all');

    const fetchData = useCallback(async () => {
        try {
            const res = await api.get(`/ceremonies/${ceremonyId}/detail`);
            setCeremony(res.data.ceremony);
            setAwards(res.data.awards || []);
            setNominations(res.data.nominations || []);
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to load ceremony', 'error');
        } finally {
            setLoading(false);
        }
    }, [ceremonyId, showSnackbar]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Group nominations under award -> year level. Include awards that have
    // nominations but are no longer linked so nothing disappears.
    const groups = useMemo(() => {
        const awardMap = new Map();
        awards.forEach(a => awardMap.set(a.id, { id: a.id, payload: a.payload, years: new Map() }));
        nominations.forEach(n => {
            if (!awardMap.has(n.award_id)) {
                awardMap.set(n.award_id, { id: n.award_id, payload: n.award_payload, years: new Map() });
            }
            if (filter !== 'all' && n.status !== filter) return;
            const group = awardMap.get(n.award_id);
            const key = n.year_level || '';
            if (!group.years.has(key)) group.years.set(key, []);
            group.years.get(key).push(n);
        });
        return [...awardMap.values()];
    }, [awards, nominations, filter]);

    const counts = useMemo(() => nominations.reduce((acc, n) => {
        acc[n.status] = (acc[n.status] || 0) + 1;
        return acc;
    }, {}), [nominations]);

    const setStatus = async (nom, status) => {
        try {
            await api.put(`/ceremonies/${ceremonyId}/nominations/${nom.id}`, { status });
            showSnackbar(status === 'winner' ? 'Winner selected' : `Marked ${status}`, 'success');
            fetchData();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Update failed', 'error');
        }
    };

    const removeNomination = async (nom) => {
        try {
            await api.delete(`/ceremonies/${ceremonyId}/nominations/${nom.id}`);
            showSnackbar('Nomination removed', 'success');
            fetchData();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Delete failed', 'error');
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 15 }}><CircularProgress /></Box>;
    }
    if (!ceremony) {
        return <Box sx={{ p: 3 }}><Typography>Ceremony not found.</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/ceremonies')}><BackIcon /></IconButton>
                    <Box>
                        <Typography variant="h4">{ceremony.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Ceremony {formatDate(ceremony.ceremony_date)} · Nominations {formatDate(ceremony.open_date)} – {formatDate(ceremony.cutoff_date)}
                        </Typography>
                    </Box>
                    {ceremony.is_locked
                        ? <Chip label="Locked" color="error" size="small" />
                        : ceremony.is_open
                            ? <Chip label="Open" color="success" size="small" />
                            : <Chip label="Not yet open" size="small" />}
                </Box>
                {(ceremony.is_open || canManage) && (
                    <Button
                        variant="contained"
                        startIcon={<NominateIcon />}
                        onClick={() => navigate(`/nominations/${ceremony.id}`, { state: { ceremonyName: ceremony.name } })}
                    >
                        Nominate
                    </Button>
                )}
            </Box>

            <ToggleButtonGroup
                size="small"
                exclusive
                value={filter}
                onChange={(e, v) => v && setFilter(v)}
                sx={{ mb: 3 }}
            >
                <ToggleButton value="all">All ({nominations.length})</ToggleButton>
                {Object.keys(STATUS_COLOURS).map(s => (
                    <ToggleButton key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                        {s} ({counts[s] || 0})
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {groups.length === 0 && (
                <Typography color="text.secondary">
                    No awards are linked to this ceremony yet. {canManage && 'Edit the ceremony to add awards.'}
                </Typography>
            )}

            {groups.map(group => {
                const yearKeys = [...group.years.keys()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
                return (
                    <Paper key={group.id} sx={{ mb: 3, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.100', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6">{group.payload?.aw_1 || 'Untitled Award'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {[group.payload?.aw_2, group.payload?.aw_3 && `Year ${group.payload.aw_3}`].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                        </Box>
                        {yearKeys.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No nominations{filter !== 'all' ? ` with status "${filter}"` : ''}.
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Year</strong></TableCell>
                                        <TableCell><strong>Nominee</strong></TableCell>
                                        <TableCell><strong>Nominated by</strong></TableCell>
                                        <TableCell><strong>Reason</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        {canManage && <TableCell align="right"><strong>Actions</strong></TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {yearKeys.flatMap(year => group.years.get(year).map(n => (
                                        <TableRow key={n.id} hover selected={n.status === 'winner'}>
                                            <TableCell>{year || '—'}</TableCell>
                                            <TableCell>
                                                {n.status === 'winner' && <TrophyIcon fontSize="small" color="warning" sx={{ mr: 0.5, verticalAlign: 'middle' }} />}
                                                {studentName(n.student_payload)}
                                            </TableCell>
                                            <TableCell>{n.nominator_name || '—'}</TableCell>
                                            <TableCell sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{n.reason || '—'}</TableCell>
                                            <TableCell>
                                                <Chip label={n.status} color={STATUS_COLOURS[n.status]} size="small" sx={{ textTransform: 'capitalize' }} />
                                            </TableCell>
                                            {canManage && (
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    {n.status !== 'shortlisted' && (
                                                        <Button size="small" onClick={() => setStatus(n, 'shortlisted')}>Shortlist</Button>
                                                    )}
                                                    {n.status !== 'winner' && (
                                                        <Button size="small" color="success" onClick={() => setStatus(n, 'winner')}>Winner</Button>
                                                    )}
                                                    {n.status !== 'declined' && (
                                                        <Button size="small" color="error" onClick={() => setStatus(n, 'declined')}>Decline</Button>
                                                    )}
                                                    {n.status !== 'pending' && (
                                                        <Button size="small" color="inherit" onClick={() => setStatus(n, 'pending')}>Reset</Button>
                                                    )}
                                                    <Tooltip title="Delete nomination">
                                                        <IconButton size="small" color="error" onClick={() => removeNomination(n)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                );
            })}
        </Box>
    );
}
